import type { WorkingCalendar, WorkingCalendarException } from '@/types';

/**
 * Phase 30.33: working-calendar computations.
 *
 * All date inputs/outputs are ISO yyyy-mm-dd strings. Date math uses
 * local-noon anchoring to sidestep DST edge cases (a T12:00 anchor is
 * safely mid-day so ±1 day arithmetic never crosses a DST boundary
 * mid-flight).
 *
 * A day is "working" iff:
 *   - The weekly bitmask has that day-of-week bit set, AND
 *   - No exception with is_working=false covers that date, OR
 *   - Some exception with is_working=true covers that date (override)
 *
 * The override rule wins: if any exception covers a date with
 * is_working=true, the day is working regardless of the weekly pattern
 * or other exceptions. Otherwise, if any exception covers with
 * is_working=false, the day is non-working. Otherwise fall back to the
 * weekly bitmask.
 */

/** Default calendar behavior: Mon-Fri working, no exceptions. */
export const DEFAULT_WORKING_BITMASK = 62; // 0111110 — bits 1..5 (Mon..Fri)

/** Anchor a yyyy-mm-dd string to a local Date at noon to avoid DST wobble. */
function toDate(iso: string): Date {
  return new Date(iso + 'T12:00:00');
}

function toIso(d: Date): string {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
}

/** Returns true iff the day-of-week bit for `date` is set in the bitmask. */
function isWeeklyWorking(date: Date, bitmask: number): boolean {
  const dow = date.getDay(); // 0=Sun ... 6=Sat
  return (bitmask & (1 << dow)) !== 0;
}

/**
 * Returns true iff `iso` falls within [ex.dateFrom, ex.dateTo] (inclusive).
 * All-string comparison works because ISO yyyy-mm-dd sorts lexically.
 */
function exceptionCoversDate(ex: WorkingCalendarException, iso: string): boolean {
  return ex.dateFrom <= iso && iso <= ex.dateTo;
}

/**
 * Check whether a specific ISO date is a working day for the given calendar.
 *
 * If calendar is null/undefined, fall back to Mon-Fri with no exceptions
 * (matches the legacy behavior before Phase 30.33).
 */
export function isWorkingDay(
  iso: string,
  calendar: WorkingCalendar | null | undefined,
  exceptions: WorkingCalendarException[],
): boolean {
  const bitmask = calendar?.workingDaysBitmask ?? DEFAULT_WORKING_BITMASK;
  const date = toDate(iso);

  // Override rule: any working_override exception covering this date makes it working.
  const relevant = exceptions.filter((e) => exceptionCoversDate(e, iso));
  const hasWorkingOverride = relevant.some((e) => e.isWorking);
  if (hasWorkingOverride) return true;

  const hasNonWorkingOverride = relevant.some((e) => !e.isWorking);
  if (hasNonWorkingOverride) return false;

  return isWeeklyWorking(date, bitmask);
}

/**
 * Count the number of WORKING days between two dates (inclusive on both
 * ends). If start > end, returns 0.
 *
 * Complexity: O(N) in days between. Safe up to ~10 years without perf
 * concerns for scheduling contexts.
 */
export function workingDaysBetween(
  startIso: string,
  endIso: string,
  calendar: WorkingCalendar | null | undefined,
  exceptions: WorkingCalendarException[],
): number {
  if (!startIso || !endIso) return 0;
  if (startIso > endIso) return 0;
  const start = toDate(startIso);
  const end = toDate(endIso);
  let count = 0;
  const cursor = new Date(start);
  let safety = 0;
  while (cursor <= end && safety < 10_000) {
    if (isWorkingDay(toIso(cursor), calendar, exceptions)) count++;
    cursor.setDate(cursor.getDate() + 1);
    safety++;
  }
  return count;
}

/**
 * Given a start date and a working-day duration, return the ISO date of
 * the LAST working day (inclusive). Skips non-working days.
 *
 * Special case: duration=0 (milestone) returns the start date unchanged.
 * Special case: if start itself is non-working, walks forward to the first
 * working day and then counts from there.
 */
export function addWorkingDays(
  startIso: string,
  durationWorkingDays: number,
  calendar: WorkingCalendar | null | undefined,
  exceptions: WorkingCalendarException[],
): string {
  if (!startIso) return '';
  if (durationWorkingDays <= 0) return startIso;
  const cursor = toDate(startIso);
  let workingSeen = 0;
  let lastWorkingIso = toIso(cursor);
  let safety = 0;
  while (workingSeen < durationWorkingDays && safety < 10_000) {
    const iso = toIso(cursor);
    if (isWorkingDay(iso, calendar, exceptions)) {
      workingSeen++;
      lastWorkingIso = iso;
      if (workingSeen >= durationWorkingDays) break;
    }
    cursor.setDate(cursor.getDate() + 1);
    safety++;
  }
  return lastWorkingIso;
}

/**
 * Shift an ISO date forward by N CALENDAR days, then walk to the next
 * working day if the resulting date is non-working. Used by cascade logic
 * that needs to add slack in raw days but end on a working day.
 */
export function shiftToNextWorkingDay(
  iso: string,
  calendar: WorkingCalendar | null | undefined,
  exceptions: WorkingCalendarException[],
): string {
  if (!iso) return iso;
  const cursor = toDate(iso);
  let safety = 0;
  while (safety < 400) {
    const candidate = toIso(cursor);
    if (isWorkingDay(candidate, calendar, exceptions)) return candidate;
    cursor.setDate(cursor.getDate() + 1);
    safety++;
  }
  return iso;
}

/** Human-friendly labels for exception types. */
export function exceptionTypeLabel(t: WorkingCalendarException['exceptionType']): string {
  switch (t) {
    case 'holiday': return 'Holiday';
    case 'code_freeze': return 'Code Freeze';
    case 'non_working': return 'Non-working';
    case 'working_override': return 'Working override';
  }
}

/**
 * Convert a bitmask to an array of 7 booleans, one per day-of-week
 * (index 0 = Sunday). Useful for rendering checkboxes.
 */
export function bitmaskToArray(bitmask: number): boolean[] {
  return Array.from({ length: 7 }, (_, i) => (bitmask & (1 << i)) !== 0);
}

/** Convert an array of 7 booleans back to a bitmask. */
export function arrayToBitmask(arr: boolean[]): number {
  let out = 0;
  for (let i = 0; i < 7; i++) if (arr[i]) out |= 1 << i;
  return out;
}
